import { curry, apply } from "ramda"
import { useEffect, useState } from "react"

/**
 * Debounce handler
 *
 * @param  delayMs {Number} Debounce delay in ms
 * @param  fn {Function} Function to debounce
 *
 * @return {void}
 *
 * @example
 *
 *    const say = (x) => console.log(x)
 *    const debouncedSay = debounced(1000, say);
 *
 *    debouncedSay("1")
 *    debouncedSay("2")
 *    debouncedSay("3")
 *
 */
export const debounced = curry((delayMs, fn) => (...args) => {
  let inDebounce

  clearTimeout(inDebounce)
  inDebounce = setTimeout(() => apply(fn, args), delayMs)
})

export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => clearTimeout(handler)
  }, [delay, value])

  return debouncedValue
}
